/**
 * 桌面端桥接：同一套前端代码在浏览器与 Tauri 里都要能跑。
 *
 * 浏览器缺的是「被远程控制」这一项 —— 页面永远不可能移动系统光标。
 * 因此在浏览器里这些接口降级为「不可用」，界面据此隐藏相关入口，
 * 而不是让用户点了没反应。
 */

import type { ShareCtrlKey, ShareCtrlMouse } from './messages'

/** Tauri 注入的调用入口。浏览器环境下不存在。 */
interface TauriGlobal {
  core: {
    invoke<T = unknown>(cmd: string, args?: Record<string, unknown>): Promise<T>
  }
  /** 事件订阅。withGlobalTauri 下才有；老版本可能缺，故可选。 */
  event?: {
    listen<T = unknown>(
      event: string,
      handler: (e: { payload: T }) => void,
    ): Promise<() => void>
  }
}

declare global {
  interface Window {
    __TAURI__?: TauriGlobal
  }
}

/** 本机能力描述。 */
export interface Capabilities {
  platform: string
  /** 是否支持接受远程控制 */
  remoteInput: boolean
  /** 能否把本机当局域网会议节点（桌面、iOS 能；安卓与网页版不能） */
  localNode: boolean
  /** 需要用户手动授予的系统权限说明 */
  permissionHint?: string
}

/**
 * 当前是否运行在原生客户端（Tauri）里 —— 桌面 App 与手机 App 都算。
 * 名字沿用 isDesktop 是历史原因：绝大多数调用点关心的是「页面来自内嵌资源、
 * 要走线路表/跨域」，这一点手机 App 与桌面 App 完全一样。
 * 只有「能不能被远程操作」这类真正的桌面能力才需要再问 isMobileApp()。
 */
export function isDesktop(): boolean {
  return typeof window !== 'undefined' && !!window.__TAURI__
}

/** 是否手机 App（Tauri Mobile）。按 UA 判：iOS 的 WKWebView 与 Android WebView 都会带上系统标识。 */
export function isMobileApp(): boolean {
  return isDesktop() && /iPhone|iPad|iPod|Android/i.test(navigator.userAgent)
}

/** 调用桌面端原生命令。非桌面环境抛错。 */
async function invoke<T>(cmd: string, args?: Record<string, unknown>): Promise<T> {
  const t = window.__TAURI__
  if (!t) throw new Error('当前不在桌面客户端中运行')
  return t.core.invoke<T>(cmd, args)
}

/** 查询本机能力。浏览器环境返回「不支持远程控制」。 */
export async function capabilities(): Promise<Capabilities> {
  if (!isDesktop()) {
    return {
      platform: 'web',
      remoteInput: false,
      localNode: false,
      permissionHint: '网页版无法被远程协助，如需此功能请使用桌面客户端。',
    }
  }
  const raw = await invoke<{
    platform: string
    remote_input: boolean
    local_node?: boolean
    permission_hint?: string | null
  }>('capabilities')
  return {
    platform: raw.platform,
    remoteInput: raw.remote_input,
    // 缺字段按不能处理：宁可不显示入口，也不给一个点下去才报「命令不存在」的按钮。
    localNode: raw.local_node ?? false,
    permissionHint: raw.permission_hint ?? undefined,
  }
}

/**
 * 授权某席位远程控制本机。
 *
 * 这是整条远程控制链路上唯一的闸门：Rust 侧只核对「指令来源 == 被授权的席位」，
 * 它并不知道这次授权是不是本机用户点的。所以只能从本机用户点「同意」的那个回调里调用
 * （见 roomStore.replyControl），绝不能因为收到服务端的任何消息而调用 ——
 * 局域网节点模式下服务端可能是任意一台同事的设备。
 */
export async function grantRemoteControl(userId: number): Promise<void> {
  if (!isDesktop()) return
  await invoke('grant_remote_control', { userId })
}

/** 收回远程控制授权。 */
export async function revokeRemoteControl(): Promise<void> {
  if (!isDesktop()) return
  await invoke('revoke_remote_control')
}

/** 查询当前持有控制权的席位。 */
export async function remoteController(): Promise<number | null> {
  if (!isDesktop()) return null
  return invoke<number | null>('remote_controller')
}

/**
 * 执行一条远程鼠标指令。
 *
 * Rust 侧会拒绝 from 不是已授权席位的指令，所以「冒充别的席位」没用；
 * 但它防不住「授权本身是怎么来的」—— 那一道在 grantRemoteControl 的调用点上，
 * 只有本机用户亲手点了同意才会授权。
 */
export async function applyRemoteMouse(from: number, ev: ShareCtrlMouse): Promise<void> {
  if (!isDesktop()) return
  await invoke('remote_mouse', { from, ev })
}

/** 执行一条远程键盘指令。 */
export async function applyRemoteKey(from: number, ev: ShareCtrlKey): Promise<void> {
  if (!isDesktop()) return
  await invoke('remote_key', { from, ev })
}
